import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  Badge,
  alpha,
  FormControl,
  Select,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
} from '@mui/material';
import {
  Logout as LogoutIcon,
  AccountCircle as AccountCircleIcon,
  SwapHoriz as SwapHorizIcon,
  Notifications as NotificationsIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useProviderInfo, PROVIDER_INFO_QUERY_KEY } from '../hooks/useProviderInfo';
import apiService from '../services/api';
import { ProviderInfo } from '../types/foundObject';
import { getProviderLogoUrl } from '../utils/imageUrl';
import { DASHBOARD_BRAND_PRIMARY, SIDEBAR_BRAND_BG } from '../theme/dashboardBrand';
import Iconify, { sidebarIcon } from './Iconify';
import {
  getProviderDashboardMenuSettings,
} from '../utils/providerDashboardMenu';
import { ProviderPlaceProvider, useProviderPlace } from '../context/ProviderPlaceContext';
import { getProviderPlaceDisplayColor } from '../utils/providerPlaceColor';
import { formatDashboardUserDisplayName } from '../utils/providerDashboardPlace';
import { useUnreadMessageNotifications } from '../hooks/useUnreadMessageNotifications';
import { useConversationsUnreadCount } from '../hooks/useConversationsUnreadCount';
import { useFoundObjectStatsQuery } from '../hooks/useFoundObjectStatsQuery';
import {
  CONVERSATIONS_UNREAD_COUNT_QUERY_KEY,
  FOUND_OBJECT_STATS_QUERY_KEY,
} from '../lib/messagingQueryCache';

type MenuBadgeVariant = 'none' | 'unread' | 'blue' | 'green' | 'orange' | 'violet';

const BADGE_BLUE = DASHBOARD_BRAND_PRIMARY;
const BADGE_GREEN = '#4CAF50';
const BADGE_ORANGE = '#FF9800';
const BADGE_VIOLET = '#7E57C2';
const DASHBOARD_APP_VERSION = '2.0';

function MenuIconWithBadge(props: {
  children: React.ReactNode;
  count: number;
  variant: MenuBadgeVariant;
}) {
  const { children, count, variant } = props;
  if (variant === 'none' || count <= 0) {
    return <>{children}</>;
  }
  if (variant === 'unread') {
    return (
      <Badge color="error" badgeContent={count} max={99}>
        {children}
      </Badge>
    );
  }
  const bg =
    variant === 'blue'
      ? BADGE_BLUE
      : variant === 'green'
        ? BADGE_GREEN
        : variant === 'orange'
          ? BADGE_ORANGE
          : BADGE_VIOLET;
  return (
    <Badge
      badgeContent={count}
      max={99}
      sx={{
        '& .MuiBadge-badge': {
          bgcolor: bg,
          color: '#fff',
          fontWeight: 600,
        },
      }}
    >
      {children}
    </Badge>
  );
}

const drawerWidth = 280;
const APP_BAR_MOBILE = 64;
const APP_BAR_DESKTOP = 92;
const SHERLOOK_LOGO_SRC = `${process.env.PUBLIC_URL}/assets/sherlook-logo.svg`;

type MenuItemConfig = {
  label: string;
  icon: React.ReactNode;
  path: string;
  badgeCount: number;
  badgeVariant: MenuBadgeVariant;
};

interface LayoutProps {
  children: React.ReactNode;
}

interface LayoutFrameProps {
  children: React.ReactNode;
  providerInfo: ProviderInfo;
}

const LayoutFrame: React.FC<LayoutFrameProps> = ({ children, providerInfo }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { activePlaces, selectedPlaceId, setSelectedPlaceId, hasMultiplePlaces, isReady } =
    useProviderPlace();

  const user = apiService.getCurrentUser();
  const userDisplayName = formatDashboardUserDisplayName(user);

  const isOnMessagesPage = location.pathname === '/messages';

  const { data: unreadBadgeData } = useConversationsUnreadCount({ poll: true });
  const unreadMessages = unreadBadgeData?.count ?? 0;

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      void queryClient.invalidateQueries({ queryKey: CONVERSATIONS_UNREAD_COUNT_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: FOUND_OBJECT_STATS_QUERY_KEY });
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [queryClient]);

  const { toast: unreadToast, closeToast: closeUnreadToast, requestNotificationPermission } =
    useUnreadMessageNotifications({
      unreadCount: unreadMessages,
      suppressToast: isOnMessagesPage,
    });

  const handleMessagesNotificationClick = useCallback(async () => {
    void requestNotificationPermission();
    navigate('/messages');
  }, [navigate, requestNotificationPermission]);

  const { data: foundObjectStats } = useFoundObjectStatsQuery({ poll: true });

  const menuItems: MenuItemConfig[] = useMemo(() => {
    const foundCount = foundObjectStats?.available ?? 0;
    const claimedCount = foundObjectStats?.claimed ?? 0;
    const toShipCount = foundObjectStats?.to_ship ?? 0;
    const inTransitCount = foundObjectStats?.in_transit ?? 0;
    const dashboardMenu = getProviderDashboardMenuSettings(providerInfo);

    const items: MenuItemConfig[] = [
      {
        label: 'Tableau de bord',
        icon: sidebarIcon('dashboard'),
        path: '/dashboard',
        badgeCount: 0,
        badgeVariant: 'none',
      },
      {
        label: 'Objets trouvés',
        icon: sidebarIcon('foundObjects'),
        path: '/found-objects',
        badgeCount: foundCount,
        badgeVariant: 'blue',
      },
      {
        label: 'Objets restitués',
        icon: sidebarIcon('returnedObjects'),
        path: '/returned-objects',
        badgeCount: claimedCount,
        badgeVariant: 'green',
      },
      {
        label: 'Objets à envoyer',
        icon: sidebarIcon('objectsToSend'),
        path: '/objects-to-send',
        badgeCount: toShipCount,
        badgeVariant: 'orange',
      },
      {
        label: 'Parti en livraison',
        icon: sidebarIcon('objectsInTransit'),
        path: '/objects-in-transit',
        badgeCount: inTransitCount,
        badgeVariant: 'violet',
      },
      {
        label: 'Stockage',
        icon: sidebarIcon('storage'),
        path: '/storage',
        badgeCount: 0,
        badgeVariant: 'none',
      },
    ];
    if (providerInfo?.provider_type?.annual_event) {
      items.push({
        label: 'Événements',
        icon: sidebarIcon('events'),
        path: '/provider-events',
        badgeCount: 0,
        badgeVariant: 'none',
      });
    }
    if (dashboardMenu.matchings_enabled) {
      items.push({
        label: 'Mes matchings',
        icon: sidebarIcon('matches'),
        path: '/matches',
        badgeCount: 0,
        badgeVariant: 'none',
      });
    }
    if (dashboardMenu.linked_announcements_enabled) {
      items.push({
        label: 'Annonces liées (clients)',
        icon: sidebarIcon('linkedAnnouncements'),
        path: '/linked-announcements',
        badgeCount: 0,
        badgeVariant: 'none',
      });
    }
    items.push(
      {
        label: 'Messages',
        icon: sidebarIcon('messages'),
        path: '/messages',
        badgeCount: unreadMessages,
        badgeVariant: 'unread',
      },
      {
        label: 'Paramètres',
        icon: sidebarIcon('settings'),
        path: '/settings',
        badgeCount: 0,
        badgeVariant: 'none',
      }
    );
    return items;
  }, [providerInfo, unreadMessages, foundObjectStats]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    apiService.logout();
    queryClient.removeQueries({ queryKey: PROVIDER_INFO_QUERY_KEY });
    navigate('/login');
    handleMenuClose();
  };

  const handleChangeEstablishment = () => {
    apiService.clearProviderDashboardContext();
    handleMenuClose();
    navigate('/select-provider');
  };

  const logoSrc =
    providerInfo &&
    getProviderLogoUrl(providerInfo.logo_url ?? undefined, providerInfo.logo ?? undefined);

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box
        sx={{
          px: 2,
          py: 2.5,
          bgcolor: SIDEBAR_BRAND_BG,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
          minHeight: { xs: 56, sm: 72 },
        }}
      >
        {logoSrc ? (
          <Box
            component="img"
            src={logoSrc}
            alt={providerInfo?.name || 'Logo partenaire'}
            sx={{
              maxHeight: 52,
              maxWidth: '92%',
              width: 'auto',
              objectFit: 'contain',
              display: 'block',
            }}
          />
        ) : (
          <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 800, color: 'primary.main' }}>
            {providerInfo?.name || 'Partenaire'}
          </Typography>
        )}
      </Box>
      <List sx={{ flex: 1, py: 1 }}>
        {menuItems.map((item) => {
          const isSelected = location.pathname === item.path;
          return (
            <ListItem key={item.path} disablePadding>
              <ListItemButton
                selected={isSelected}
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) {
                    setMobileOpen(false);
                  }
                }}
              >
                <ListItemIcon sx={{ color: isSelected ? 'primary.main' : 'text.secondary' }}>
                  <MenuIconWithBadge count={item.badgeCount} variant={item.badgeVariant}>
                    {item.icon}
                  </MenuIconWithBadge>
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  sx={{
                    '& .MuiListItemText-primary': {
                      fontWeight: isSelected ? 800 : 600,
                      color: isSelected ? 'text.primary' : 'text.secondary',
                      fontSize: '0.92rem',
                    },
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
      <Box
        sx={{
          px: 2,
          py: 2,
          mt: 'auto',
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.75,
          flexShrink: 0,
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
          Version {DASHBOARD_APP_VERSION}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: '0.68rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}
        >
          Powered by
        </Typography>
        <Box
          component="img"
          src={SHERLOOK_LOGO_SRC}
          alt="Sherlook"
          sx={{
            width: 'auto',
            maxWidth: '88%',
            height: 30,
            objectFit: 'contain',
            display: 'block',
          }}
        />
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          boxShadow: 'none',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          bgcolor: alpha(theme.palette.background.default, 0.72),
          color: 'text.primary',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Toolbar sx={{ minHeight: { xs: APP_BAR_MOBILE, md: APP_BAR_DESKTOP }, px: { xs: 1.5, md: 3 } }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' }, color: 'text.primary' }}
          >
            <Iconify icon="eva:menu-2-fill" width={24} height={24} />
          </IconButton>
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{
              flexGrow: 1,
              minWidth: 0,
              fontSize: { xs: '0.95rem', sm: undefined },
            }}
          >
            Dashboard {providerInfo?.name ? `- ${providerInfo.name}` : 'Partenaire'}
          </Typography>
          <Box display="flex" alignItems="center" gap={{ xs: 0.5, sm: 1.5 }} sx={{ flexShrink: 0 }}>
            {hasMultiplePlaces && (
              <FormControl
                size="small"
                sx={{
                  minWidth: { xs: 108, sm: 168 },
                  maxWidth: { xs: 132, sm: 220 },
                  display: { xs: 'none', sm: 'block' },
                }}
              >
                <Select
                  value={selectedPlaceId ?? ''}
                  onChange={(e) => setSelectedPlaceId(Number(e.target.value))}
                  displayEmpty
                  renderValue={(value) => {
                    const place = activePlaces.find((p) => p.id === Number(value));
                    const label = place?.display_name || place?.name || 'Lieu';
                    const color = getProviderPlaceDisplayColor(place);
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            bgcolor: color,
                            flexShrink: 0,
                          }}
                        />
                        <Box
                          component="span"
                          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                          {label}
                        </Box>
                      </Box>
                    );
                  }}
                  sx={{
                    bgcolor: 'background.paper',
                    fontSize: '0.875rem',
                    '& .MuiSelect-select': { py: 0.75 },
                  }}
                >
                  {activePlaces.map((place) => {
                    const color = getProviderPlaceDisplayColor(place);
                    return (
                      <MenuItem key={place.id} value={place.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              bgcolor: color,
                              flexShrink: 0,
                            }}
                          />
                          {place.display_name || place.name}
                        </Box>
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            )}
            <Typography variant="body2" sx={{ mr: { xs: 0, sm: 1 }, display: { xs: 'none', lg: 'block' } }} noWrap>
              {userDisplayName}
            </Typography>
            <IconButton
              color="inherit"
              onClick={() => void handleMessagesNotificationClick()}
              title="Messages non lus"
              aria-label={
                unreadMessages > 0
                  ? `${unreadMessages} message${unreadMessages > 1 ? 's' : ''} non lu${unreadMessages > 1 ? 's' : ''}`
                  : 'Messages'
              }
              sx={{ color: 'text.primary' }}
            >
              <Badge
                badgeContent={unreadMessages > 99 ? '99+' : unreadMessages}
                color="error"
                invisible={unreadMessages === 0}
              >
                <NotificationsIcon />
              </Badge>
            </IconButton>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenuOpen}
              color="inherit"
              sx={{ color: 'text.primary' }}
            >
              <Avatar sx={{ width: 32, height: 32 }}>
                <AccountCircleIcon />
              </Avatar>
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              {hasMultiplePlaces && (
                <Box sx={{ px: 2, py: 1.5, display: { xs: 'block', sm: 'none' }, minWidth: 220 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                    Lieu
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={selectedPlaceId ?? ''}
                      onChange={(e) => {
                        setSelectedPlaceId(Number(e.target.value));
                        handleMenuClose();
                      }}
                      displayEmpty
                    >
                      {activePlaces.map((place) => {
                        const color = getProviderPlaceDisplayColor(place);
                        return (
                          <MenuItem key={place.id} value={place.id}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box
                                sx={{
                                  width: 10,
                                  height: 10,
                                  borderRadius: '50%',
                                  bgcolor: color,
                                  flexShrink: 0,
                                }}
                              />
                              {place.display_name || place.name}
                            </Box>
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Box>
              )}
              {apiService.isCurrentUserSuperAdmin() && (
                <MenuItem onClick={handleChangeEstablishment}>
                  <ListItemIcon>
                    <SwapHorizIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Changer d&apos;établissement</ListItemText>
                </MenuItem>
              )}
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Déconnexion</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: 'background.default',
              borderRight: '1px solid',
              borderColor: 'divider',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: 'background.default',
              borderRight: '1px solid',
              borderColor: 'divider',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: isOnMessagesPage
            ? { xs: 0, md: 3 }
            : { xs: 2, md: 3 },
          width: { md: `calc(100% - ${drawerWidth}px)` },
          maxWidth: '100%',
          overflowX: 'hidden',
          pt: isOnMessagesPage
            ? { xs: `${APP_BAR_MOBILE}px`, md: `${APP_BAR_DESKTOP + 24}px` }
            : { xs: `${APP_BAR_MOBILE + 16}px`, md: `${APP_BAR_DESKTOP + 24}px` },
          pb: isOnMessagesPage ? { xs: 0, md: 5 } : { xs: 3, md: 5 },
          display: isOnMessagesPage ? { xs: 'flex', md: 'block' } : 'block',
          flexDirection: 'column',
          minHeight: isOnMessagesPage ? { xs: '100dvh', md: 'auto' } : undefined,
        }}
      >
        {isReady ? children : (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        )}
      </Box>
      <Snackbar
        open={unreadToast.open}
        autoHideDuration={6000}
        onClose={closeUnreadToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity="info"
          onClose={closeUnreadToast}
          icon={<NotificationsIcon fontSize="inherit" />}
          action={
            <Button color="inherit" size="small" onClick={() => void handleMessagesNotificationClick()}>
              Voir
            </Button>
          }
          sx={{ width: '100%' }}
        >
          {unreadToast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { data: providerInfo, isLoading, error } = useProviderInfo();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !providerInfo?.id) {
    return (
      <Box p={3}>
        <Alert severity="warning">
          Impossible de charger votre fiche partenaire. Reconnectez-vous ou contactez le support.
        </Alert>
      </Box>
    );
  }

  return (
    <ProviderPlaceProvider providerId={providerInfo.id}>
      <LayoutFrame providerInfo={providerInfo}>{children}</LayoutFrame>
    </ProviderPlaceProvider>
  );
};

export default Layout;